import { useCallback, useEffect, useRef } from 'react';
import { pollImageBatchStatus } from '../../../../services/conversationHistoryService';
import { debug } from '../../../../utils/debugLogger';

interface UseImageBatchPollingOptions {
  threadId: string | null;
  /** Start polling only when this is true. */
  enabled: boolean;
  idToken: string;
  /** Called when polling returns status === "completed" with the generated image URLs. */
  onCompleted: (imageUrls: string[]) => void;
  /** Called when polling returns status === "failed" or max duration is reached. */
  onFailed: (message: string) => void;
  /** Milliseconds between polls (default: 3 seconds — batches finish in 30-180s, much
   *  faster than deep research, so poll more frequently). */
  intervalMs?: number;
  /** Stop polling after this many ms regardless of status (default: 5 minutes — generous
   *  margin over IMAGE_BATCH_POLL_TIMEOUT's 200s backend timeout). */
  maxDurationMs?: number;
}

export function useImageBatchPolling({
  threadId,
  enabled,
  idToken,
  onCompleted,
  onFailed,
  intervalMs = 3_000,
  maxDurationMs = 5 * 60_000,
}: UseImageBatchPollingOptions): { stop: () => void } {
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startedAtRef = useRef<number>(0);
  const stoppedRef = useRef(false);

  const stop = useCallback(() => {
    stoppedRef.current = true;
    if (timerRef.current !== null) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!enabled || !threadId) return;

    stoppedRef.current = false;
    startedAtRef.current = Date.now();

    const poll = async () => {
      if (stoppedRef.current) return;

      if (Date.now() - startedAtRef.current > maxDurationMs) {
        stop();
        onFailed(
          `Image generation timed out after ${Math.round(maxDurationMs / 60_000)} minutes.`,
        );
        return;
      }

      try {
        const result = await pollImageBatchStatus(threadId, idToken);
        debug.log('[useImageBatchPolling] status:', result.status);

        if (result.status === 'completed' && result.image_urls && result.image_urls.length > 0) {
          stop();
          onCompleted(result.image_urls);
        } else if (result.status === 'failed') {
          stop();
          onFailed('Image generation failed.');
        }
        // 'in_progress' or 'none' → keep polling
      } catch (err) {
        debug.error('[useImageBatchPolling] poll error (will retry):', err);
        // Network errors are transient — keep polling
      }
    };

    // Poll once immediately, then on interval
    void poll();
    timerRef.current = setInterval(poll, intervalMs);

    return stop;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, threadId, idToken]);

  return { stop };
}
